import { yupResolver } from '@hookform/resolvers/yup';

/*  MUI  */
import { Box, Button, Typography } from '@mui/material';

/*  Shared  */
import { Logo } from 'application/Shared/Components/App';

/*  Components  */
import { Form, FormTextInput } from 'application/Shared/Components/Form';
import { FontSizes } from 'application/Shared/Constants';
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';

/* Hooks */
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';
import * as yup from 'yup';

import Okta from './Components/Okta/Containers/Button';
import {
    AUTH_POPUP_WINDOW_FEATURES,
    AUTH_POPUP_WINDOW_TARGET
} from './Constants';
import { LoginLayout } from './LoginLayout';
import { LoginLegacy } from './LoginLegacy';

const validator = (t) =>
    yup.object({
        email: yup
            .string()
            .email(t('Form.validEmail'))
            .trim()
            .required(t('Form.fieldRequired'))
    });

const onSubmit = async (values, setLoginType, setPopup) => {
    // Reset the loginType so that if the user closes the popup and just clicks the button again,
    // it'll properly open the popup again.
    setLoginType('');

    // Since we have added an ajax call to this button, the Okta popup window will be blocked by browsers.
    // To avoid this, we open popup first, then pass the popup variable into the Okta component below,
    // so that it can use the same popup and change the URL to the Okta login flow.
    // Using the solution suggested here - https://stackoverflow.com/a/25050893
    const loadingUrl = `${window.origin}/loading`;
    const popup = window.open(
        loadingUrl,
        AUTH_POPUP_WINDOW_TARGET,
        AUTH_POPUP_WINDOW_FEATURES
    );
    setPopup(popup);

    const { email } = values;

    try {
        const response = await axios.post('/authentication/email', {
            email
        });

        if (!Response.has(response)) {
            debug.e('no data', response);
            return;
        }
        const { data } = response;
        const { loginType } = data;

        // If login is not 'okta', we need to close the popup since we're just navigating to the /login/legacy route below.
        if (loginType !== 'okta') {
            popup?.close();
        }
        setLoginType(loginType);
    } catch (e) {
        debug.e(e);
        Notify.e(e?.response?.data?.message || e.message);
    }
};

const AnimatedBox = ({ children, ...rest }) => (
    <motion.div
        style={{
            position: 'absolute',
            width: '100%'
        }}
        transition={{ type: 'tween' }}
        {...rest}
    >
        {children}
    </motion.div>
);

const LoginValidateEmail = ({ setLoginType, setPopup }) => {
    const { t } = useTranslation();
    return (
        <Form
            defaultValues={{
                email: ''
            }}
            validator={yupResolver(validator(t))}
            onSubmit={(values) => onSubmit(values, setLoginType, setPopup)}
            data-testid="loginForm"
        >
            <Box mb="24px">
                <FormTextInput
                    name="email"
                    label={t('Common.email')}
                    alignment="vertical"
                    autoFocus
                    required
                />
            </Box>
            <Button
                data-trackid="login_validateEmail_next"
                type="submit"
                variant="contained"
                color="primary"
                style={{ width: '100%' }}
            >
                {t('Common.next')}
            </Button>
        </Form>
    );
};

export const Login = () => {
    const { t } = useTranslation();
    const session = useSession();
    const { search } = useLocation();

    const [loginType, setLoginType] = useState('');
    const [popup, setPopup] = useState();

    return (
        <LoginLayout>
            {session && <Navigate to={{ pathname: '/home', search }} />}

            <Box
                data-trackid="loginLeftItemsContainer"
                width="100%"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                maxWidth="700px"
                px={['32px', '64px', '64px', '128px']}
            >
                <Logo style={{ margin: '0 0 64px 0' }} />
                <Typography
                    variant="h2"
                    marginBottom="32px"
                    sx={{
                        color: 'text.primary',
                        fontWeight: 600,
                        textTransform: 'none'
                    }}
                >
                    {t('Common.signin')}
                </Typography>

                <Box height="150px" position="relative" overflow="hidden">
                    <AnimatePresence>
                        {loginType !== 'legacy' && (
                            <AnimatedBox
                                key="0"
                                initial={{ x: 0 }}
                                exit={{ x: '-700px' }}
                            >
                                <LoginValidateEmail
                                    setLoginType={setLoginType}
                                    setPopup={setPopup}
                                />
                            </AnimatedBox>
                        )}

                        {loginType === 'legacy' && (
                            <AnimatedBox
                                key="1"
                                initial={{ x: '700px' }}
                                animate={{ x: 0 }}
                            >
                                <LoginLegacy />
                            </AnimatedBox>
                        )}
                    </AnimatePresence>
                </Box>

                {loginType === 'okta' && (
                    <Okta invisibleLogin popupWindow={popup} />
                )}
                <Box
                    style={{ fontSize: FontSizes[0] }}
                    data-trackid="loginCopyright"
                    my="16px"
                >
                    {t('Common.copyright')}
                </Box>
            </Box>
        </LoginLayout>
    );
};
